import { Loader2Icon, ShieldAlertIcon, ShieldCheckIcon } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FileRef } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { RealtimeContext } from '@/shared/realtime/context';

type ScanStatus = NonNullable<FileRef['scanStatus']>;

/** Payload of the realtime event `files.scan-completed` (backend `ScanCompletedPayload`). */
interface ScanCompleted {
  id: string;
  status: ScanStatus;
}

/**
 * Where the antivirus stands for a file (docs/modules/files.md "Antivirus scanning"): a small badge while the scan is pending
 * ("Checking for viruses…"), which turns into a check mark when the result arrives over the realtime connection
 * (`files.scan-completed`), or into a warning when malware was found — the file is gone then. Nothing is shown for files that
 * were not scanned (the antivirus is off) or for clean ones that never waited. Works without a realtime connection too (public
 * pages): the status is then the one the file was loaded with.
 */
export function FileScanBadge({
  file,
  className,
}: {
  file: Pick<FileRef, 'id' | 'scanStatus'>;
  className?: string;
}) {
  const { t } = useTranslation('shared');
  const realtime = useContext(RealtimeContext);
  const [update, setUpdate] = useState<{ fileId: string; status: ScanStatus } | null>(null);
  const loaded = file.scanStatus ?? 'skipped';
  // The result that arrived belongs to this file; a different file (the list changed under us) starts from its own status.
  const status = update?.fileId === file.id ? update.status : loaded;

  useEffect(() => {
    if (loaded !== 'pending' || !realtime) return undefined;
    return realtime.on<ScanCompleted | null>('files.scan-completed', (payload) => {
      if (payload?.id === file.id) setUpdate({ fileId: file.id, status: payload.status });
    });
  }, [realtime, loaded, file.id]);

  const arrived = loaded === 'pending' && status !== 'pending';
  if (status === 'pending') {
    return (
      <span
        role="status"
        data-testid="file-scan-pending"
        className={cn('inline-flex items-center gap-1 text-xs text-muted-foreground', className)}
      >
        <Loader2Icon className="size-3 animate-spin" aria-hidden />
        {t('files.scanPending')}
      </span>
    );
  }
  if (status === 'infected') {
    return (
      <span
        role="status"
        data-testid="file-scan-infected"
        className={cn('inline-flex items-center gap-1 text-xs font-medium text-destructive-text', className)}
      >
        <ShieldAlertIcon className="size-3" aria-hidden />
        {t('files.scanInfected')}
      </span>
    );
  }
  if (arrived && status === 'clean') {
    return (
      <span
        role="status"
        data-testid="file-scan-clean"
        className={cn('inline-flex items-center gap-1 text-xs text-success-text', className)}
      >
        <ShieldCheckIcon className="size-3" aria-hidden />
        {t('files.scanClean')}
      </span>
    );
  }
  return null;
}
